// src/pages/Login.js
import { useState, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import API from '../api/api';
import {
  Avatar,
  Button,
  TextField,
  Grid,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  InputAdornment,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useI18n } from '../contexts/I18nContext';

export default function Login() {
  const { login } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const { t } = useI18n();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const res = await API.post('/auth/login', { username, password });
      login(
        { id: res.data.id, username: res.data.username, role: res.data.role },
        res.data.token
      );
    } catch (err) {
      showSnackbar(err.response?.data?.message || t('login.failed'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: '100vh',
        background: 'var(--app-bg)',
      }}
    >
      <Grid item xs={10} sm={6} md={4}>
        <Paper
          elevation={12}
          sx={{
            padding: 4,
            borderRadius: 3,
            textAlign: 'center',
            backgroundColor: 'var(--surface-1)',
            border: '1px solid var(--border-subtle)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Avatar
              sx={{
                bgcolor: 'transparent',
                width: 56,
                height: 56,
                mb: 2,
                border: '2px solid var(--text-primary)',
              }}
            >
              <PersonIcon sx={{ fontSize: 32, color: 'var(--text-primary)' }} />
            </Avatar>
            <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
              {t('login.title')}
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              placeholder={t('login.username')}
              variant="standard"
              fullWidth
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: 'var(--text-primary)' }} />
                  </InputAdornment>
                ),
                style: { color: 'var(--text-primary)' },
              }}
              sx={{
                '& .MuiInput-underline:before': { borderBottomColor: 'var(--text-primary)' },
                '& .MuiInput-underline:after': { borderBottomColor: 'var(--text-primary)' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                  borderBottomColor: 'var(--text-primary)',
                },
              }}
            />

            <TextField
              placeholder={t('login.password')}
              type="password"
              variant="standard"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'var(--text-primary)' }} />
                  </InputAdornment>
                ),
                style: { color: 'var(--text-primary)' },
              }}
              sx={{
                '& .MuiInput-underline:before': { borderBottomColor: 'var(--text-primary)' },
                '& .MuiInput-underline:after': { borderBottomColor: 'var(--text-primary)' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                  borderBottomColor: 'var(--text-primary)',
                },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  sx={{ color: 'var(--text-primary)' }}
                />
              }
              label={<Typography sx={{ color: 'var(--text-primary)' }}>{t('login.remember')}</Typography>}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={submitting}
              sx={{
                mt: 3,
                borderRadius: 20,
                bgcolor: '#16a34a',
                color: '#ffffff',
                fontWeight: 'bold',
                opacity: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: '#15803d' },
                '&.Mui-disabled': {
                  bgcolor: '#86efac',
                  color: '#14532d',
                },
              }}
            >
              {submitting ? t('common.processing') : t('login.submit')}
            </Button>

            <Typography sx={{ mt: 2, color: 'var(--text-primary)', cursor: 'pointer' }}>
              {t('login.forgot')}
            </Typography>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
}




